import { authProvider } from './currentAuthProvider'
import { getLoginPath } from '@/utils/domain'
import { clearOrganizationReadOnly } from '@/composables/useOrganizationReadOnly'

/** Query flag telling the login page to explain why the user landed there. */
export const SESSION_EXPIRED_QUERY_PARAM = 'sessionExpired'

/** Whether the error means the user's session ended and cannot be refreshed (SCRIVIO-FE-8T). */
export function isSessionExpiredError(error: unknown): boolean {
  return authProvider.isSessionExpiredError(error)
}

/** Guards against several concurrent API failures each kicking off a redirect. */
let handling = false

/**
 * End the session explicitly: sign out and send the user to the login
 * page with a "your session expired" notice, preserving where they were so they
 * return there after signing in.
 *
 * Callers must NOT log the triggering error — an expired session is an expected
 * end-of-session condition, and the `console.error` was what turned it into
 * recurring Sentry noise via `auto.core.capture_console`.
 *
 * Signs out through the auth provider rather than the auth store: the store's
 * `onAuthStateChanged` listener reacts to it and clears the profile all the same,
 * and staying below the store keeps this importable from the axios interceptor.
 *
 * Navigates with a full page load rather than the router for the same reason (the
 * interceptor cannot import the router without a cycle), and because a reload
 * guarantees no store or in-flight component keeps operating on a dead session.
 */
export async function handleSessionExpired(): Promise<void> {
  if (handling) return
  handling = true

  try {
    await authProvider.signOut()
  } catch {
    // Signing out of an invalid session can fail; the redirect below ends it.
  }

  const loginPath = getLoginPath()
  if (window.location.pathname === loginPath) return

  const query = new URLSearchParams({
    [SESSION_EXPIRED_QUERY_PARAM]: '1',
    redirect: window.location.pathname + window.location.search,
  })
  window.location.href = `${loginPath}?${query}`
}

/**
 * Force-refresh the ID token so the caller's next request carries the
 * custom claims the backend just wrote (organizationId, permissions, admin).
 *
 * Every force-refresh in the app wants the same three things, which used to be
 * hand-rolled at each call site (#3096): skip silently when nobody is signed in,
 * get a genuinely fresh token rather than the cached one, and end the session
 * instead of marching on when the *refresh* token itself has died.
 *
 * It is also the app's one spelling of "the claims were refreshed", which is
 * what clears the organisation-read-only banner — see the call below.
 *
 * @returns the fresh ID token, or `null` when no user is signed in.
 * @throws the original provider error. A terminal auth error is routed to
 *   {@link handleSessionExpired} first but still propagates, so the caller aborts
 *   its own flow rather than continuing against a dead session. Retryable errors
 *   (e.g. `auth/network-request-failed`) pass through untouched.
 */
export async function refreshIdToken(): Promise<string | null> {
  if (!authProvider.currentSession()) return null

  try {
    const token = await authProvider.getIdToken(true)
    // A session that has just re-asked the backend who it is starts over on the
    // organisation write gate: nothing in a response says an organisation has
    // become writable again, so what the banner states is what was last
    // refused, and the next refused write re-establishes it (#4445).
    clearOrganizationReadOnly()
    return token
  } catch (error) {
    if (isSessionExpiredError(error)) {
      await handleSessionExpired()
    }
    throw error
  }
}
